import { useMutation, useQuery } from '@apollo/client';
import { produce } from 'immer';
import React, { useMemo, useState } from 'react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxOption,
  ComboboxSelectButton,
  Icon,
  Stack,
  useToast,
} from '@eluve/components';
import { FragmentOf, graphql } from '@eluve/graphql.tada';
import { useTenantIdFromParams } from '@eluve/session-helpers';

import { tenantMlEngineerFragment, userFragment } from './gql-operations';

const getUsersQuery = graphql(
  `
    query getUsersForMlEngineers {
      users {
        ...User
      }
    }
  `,
  [userFragment],
);

const addTenantMlEngineerMutation = graphql(`
  mutation addTenantMlEngineer($tenantId: uuid!, $userId: uuid!) {
    insertTenantMlEngineersOne(
      object: { tenantId: $tenantId, userId: $userId }
    ) {
      __typename
      tenantId
      userId
    }
  }
`);

export const AddTenantMlEngineer: React.FC = () => {
  const tenantId = useTenantIdFromParams()!;
  const { data } = useQuery(getUsersQuery);
  const toast = useToast();

  const existingMlEngineers = useCompleteFragment({
    fragment: tenantMlEngineerFragment,
    key: { id: tenantId },
  });

  const [selectedUser, setSelectedUser] = useState<FragmentOf<
    typeof userFragment
  > | null>(null);

  const users = useMemo(() => {
    return (
      data?.users.filter(
        (user) =>
          !existingMlEngineers.ml_engineers.some(
            (existing) => existing.userId === user.id,
          ),
      ) ?? []
    );
  }, [data, existingMlEngineers]);

  const [addTenantMlEngineer] = useMutation(addTenantMlEngineerMutation, {
    onCompleted: () => {
      toast.success('ML engineer added');
      setSelectedUser(null);
    },
    onError: () => {
      toast.error('Failed to add ML engineer');
      setSelectedUser(null);
    },
    optimisticResponse: (data) => ({
      insertTenantMlEngineersOne: {
        __typename: 'TenantMlEngineers' as const,
        tenantId: data.tenantId,
        userId: data.userId,
        createdAt: new Date().toISOString(),
        user: {
          __typename: 'Users' as const,
          id: data.userId,
          email: selectedUser!.email,
          firstName: selectedUser!.firstName,
          lastName: selectedUser!.lastName,
        },
      },
    }),
    update: (_cache, { data }) => {
      cacheUtils.updateFragment(
        {
          fragment: tenantMlEngineerFragment,
          key: { id: tenantId },
        },
        (existing) =>
          produce(existing, (draft) => {
            if (!draft) return;
            if (!data?.insertTenantMlEngineersOne) return;

            draft.ml_engineers ??= [];
            const { insertTenantMlEngineersOne } = data;
            const exists = draft.ml_engineers.some(
              (e) =>
                insertTenantMlEngineersOne &&
                e.userId === insertTenantMlEngineersOne.userId,
            );
            if (!exists) {
              draft.ml_engineers.push({
                ...insertTenantMlEngineersOne,
                createdAt: new Date().toISOString(),
                user: selectedUser!,
              });
            }
          }),
      );
    },
  });

  const addSelectedUserAsMlEngineer = async () => {
    if (!selectedUser) {
      toast.error('Please select a user');
      return;
    }

    await addTenantMlEngineer({
      variables: {
        tenantId,
        userId: selectedUser.id,
      },
    });
  };

  return (
    <Stack>
      <span className="text-lg font-bold text-text-primary">
        Add ML Engineer
      </span>
      <Stack gap={2} direction="horizontal">
        <Combobox>
          <ComboboxSelectButton className="min-w-80" disabled={!users.length}>
            {selectedUser ? (
              <Stack gap={0}>
                <span className="text-md font-medium text-text-primary">
                  {selectedUser.firstName} {selectedUser.lastName}
                </span>
                <span className="text-2xs font-semibold text-text-primary">
                  {selectedUser.email}
                </span>
              </Stack>
            ) : (
              'Select User'
            )}
          </ComboboxSelectButton>
          <ComboboxDropdown searchPlaceholder="Search for user">
            {users.map((u) => {
              const searchValue = [u.firstName, u.lastName, u.email].join(' ');

              return (
                <ComboboxOption
                  key={u.id}
                  searchValue={searchValue}
                  onSelect={() => setSelectedUser(u)}
                >
                  <Stack gap={2} direction="horizontal">
                    <Stack gap={0}>
                      <span className="text-md font-medium text-text-primary">
                        {u.firstName} {u.lastName}
                      </span>
                      <span className="text-2xs font-semibold text-text-primary">
                        {u.email}
                      </span>
                    </Stack>

                    {selectedUser?.id === u.id && (
                      <Icon name="Check" size="xl" />
                    )}
                  </Stack>
                </ComboboxOption>
              );
            })}
          </ComboboxDropdown>
        </Combobox>

        <Button disabled={!selectedUser} onClick={addSelectedUserAsMlEngineer}>
          Add ML Engineer
        </Button>
      </Stack>
    </Stack>
  );
};
